import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContentfulPage } from "../../context/ContentfulPageContext";
import { generateResponsiveTransformClasses } from "../../styling/ResponsiveTransform";
import { StyleManagementFab } from "../../styling/StyleManagement";
import { buildResponsiveStyles, getResponsiveValue } from "../../styling/helpers";
import { MediaItemDescription } from "./MediaItemDescription";
export const MediaWrapper = ({ media, title, id: mediaId }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s;
    const { isPreview, getContentBlockStyles, previewStyles: contextPreviewStyles } = useContentfulPage();
    if (!media || !media.fields) {
        console.warn("Media data is missing");
        return null;
    }
    const { fields } = media;
    const altText = title || fields.title || fields.description || "Media content";
    // Get media-specific styles if mediaId exists
    // Use preview styles if available for this block, otherwise use saved styles
    const savedStyles = mediaId ? getContentBlockStyles(mediaId) : {};
    const mediaStyles = contextPreviewStyles && contextPreviewStyles.blockId === mediaId ? contextPreviewStyles.styles : savedStyles;
    // Build all responsive style classes dynamically from config
    const responsiveStyles = buildResponsiveStyles(mediaStyles, "media", {
        objectFit: "object-cover",
        maxWidth: "max-w-full",
    });
    // Non-responsive styles
    const borderRadiusClass = (mediaStyles === null || mediaStyles === void 0 ? void 0 : mediaStyles.borderRadius) || "";
    // Responsive style classes
    const objectFitClass = responsiveStyles.objectFit;
    const objectPositionClass = responsiveStyles.objectPosition;
    const aspectRatioClass = responsiveStyles.aspectRatio;
    const maxWidthClass = responsiveStyles.maxWidth;
    const verticalAlignClass = responsiveStyles.verticalAlign;
    const horizontalAlignClass = responsiveStyles.horizontalAlign;
    // Description display settings
    const showDescription = getResponsiveValue(mediaStyles, "showDescription", "desktop", false);
    const descriptionTextAlignClass = responsiveStyles.descriptionTextAlign;
    // Description offset values for inline styles (responsive via CSS variables)
    const mobileDescX = (_b = (_a = mediaStyles === null || mediaStyles === void 0 ? void 0 : mediaStyles.mobile) === null || _a === void 0 ? void 0 : _a.descriptionOffsetX) !== null && _b !== void 0 ? _b : 0;
    const tabletDescX = (_c = mediaStyles === null || mediaStyles === void 0 ? void 0 : mediaStyles.tablet) === null || _c === void 0 ? void 0 : _c.descriptionOffsetX;
    const desktopDescX = (_d = mediaStyles === null || mediaStyles === void 0 ? void 0 : mediaStyles.desktop) === null || _d === void 0 ? void 0 : _d.descriptionOffsetX;
    const mobileDescY = (_f = (_e = mediaStyles === null || mediaStyles === void 0 ? void 0 : mediaStyles.mobile) === null || _e === void 0 ? void 0 : _e.descriptionOffsetY) !== null && _f !== void 0 ? _f : 0;
    const tabletDescY = (_g = mediaStyles === null || mediaStyles === void 0 ? void 0 : mediaStyles.tablet) === null || _g === void 0 ? void 0 : _g.descriptionOffsetY;
    const desktopDescY = (_h = mediaStyles === null || mediaStyles === void 0 ? void 0 : mediaStyles.desktop) === null || _h === void 0 ? void 0 : _h.descriptionOffsetY;
    // Generate unique class name for description positioning
    const descriptionClassName = mediaId ? `media-desc-${mediaId.replace(/[^a-zA-Z0-9]/g, "")}` : "";
    // Adjustable mode - check desktop setting
    const isAdjustable = getResponsiveValue(mediaStyles, "adjustable", "desktop", false);
    // Build responsive Tailwind classes for transforms
    const responsiveTransform = isAdjustable
        ? generateResponsiveTransformClasses({
            values: {
                mobile: {
                    scale: (_j = mediaStyles === null || mediaStyles === void 0 ? void 0 : mediaStyles.mobile) === null || _j === void 0 ? void 0 : _j.scale,
                    positionX: (_k = mediaStyles === null || mediaStyles === void 0 ? void 0 : mediaStyles.mobile) === null || _k === void 0 ? void 0 : _k.positionX,
                    positionY: (_l = mediaStyles === null || mediaStyles === void 0 ? void 0 : mediaStyles.mobile) === null || _l === void 0 ? void 0 : _l.positionY,
                },
                tablet: {
                    scale: (_m = mediaStyles === null || mediaStyles === void 0 ? void 0 : mediaStyles.tablet) === null || _m === void 0 ? void 0 : _m.scale,
                    positionX: (_o = mediaStyles === null || mediaStyles === void 0 ? void 0 : mediaStyles.tablet) === null || _o === void 0 ? void 0 : _o.positionX,
                    positionY: (_p = mediaStyles === null || mediaStyles === void 0 ? void 0 : mediaStyles.tablet) === null || _p === void 0 ? void 0 : _p.positionY,
                },
                desktop: {
                    scale: (_q = mediaStyles === null || mediaStyles === void 0 ? void 0 : mediaStyles.desktop) === null || _q === void 0 ? void 0 : _q.scale,
                    positionX: (_r = mediaStyles === null || mediaStyles === void 0 ? void 0 : mediaStyles.desktop) === null || _r === void 0 ? void 0 : _r.positionX,
                    positionY: (_s = mediaStyles === null || mediaStyles === void 0 ? void 0 : mediaStyles.desktop) === null || _s === void 0 ? void 0 : _s.positionY,
                },
            },
        })
        : "";
    // Container classes based on adjustable mode
    // Non-adjustable: flex container for alignment control
    // aspect ratio control
    const containerClasses = isAdjustable
        ? `relative h-full `.trim()
        : `flex h-full w-full overflow-hidden  ${aspectRatioClass} ${verticalAlignClass || "items-center"} ${horizontalAlignClass || "justify-center"}`.trim();
    // Build responsive CSS for description positioning (using inline styles via CSS custom properties)
    const responsiveDescriptionCSS = showDescription && mediaId
        ? `
		.${descriptionClassName} {
			left: ${50 + mobileDescX}%;
			top: ${50 + mobileDescY}%;
			transform: translate(-50%, -50%);
		}
		${tabletDescX !== undefined || tabletDescY !== undefined
            ? `@media (min-width: 768px) {
			.${descriptionClassName} {
				${tabletDescX !== undefined ? `left: ${50 + tabletDescX}%;` : ""}
				${tabletDescY !== undefined ? `top: ${50 + tabletDescY}%;` : ""}
			}
		}`
            : ""}
		${desktopDescX !== undefined || desktopDescY !== undefined
            ? `@media (min-width: 1024px) {
			.${descriptionClassName} {
				${desktopDescX !== undefined ? `left: ${50 + desktopDescX}%;` : ""}
				${desktopDescY !== undefined ? `top: ${50 + desktopDescY}%;` : ""}
			}
		}`
            : ""}
	`
        : "";
    // Description classes
    const descriptionClasses = `absolute ${descriptionClassName} ${descriptionTextAlignClass || "text-left"} p-2 text-white z-10`.trim();
    // Image classes based on adjustable mode
    const imageClasses = isAdjustable
        ? `${borderRadiusClass} absolute ${objectFitClass} ${objectPositionClass} ${responsiveTransform}`.trim()
        : `${borderRadiusClass} ${objectFitClass} ${objectPositionClass} ${maxWidthClass} w-full h-full`.trim();
    const previewClasses = isPreview && mediaId ? "hover:ring-2 hover:ring-inset hover:ring-orange-400 group" : "";
    // Check if media has a file attached
    if (!fields.file || !fields.file.url) {
        console.warn("Media file URL is missing");
        return null;
    }
    const { file } = fields;
    const isImage = file.contentType && file.contentType.startsWith("image/");
    if (isImage) {
        return (_jsxs("div", { className: `${containerClasses} ${previewClasses}`, children: [responsiveDescriptionCSS && _jsx("style", { children: responsiveDescriptionCSS }), _jsx("div", { className: "relative inline-block ", children: isPreview && mediaId && (_jsx(StyleManagementFab, { sectionId: mediaId, contentBlockType: "media", currentStyles: mediaStyles })) }), _jsx("img", { src: file.url.startsWith("//") ? `https:${file.url}` : file.url, alt: altText, title: altText, className: imageClasses }), showDescription && fields.description && (_jsx(MediaItemDescription, { classes: descriptionClasses, description: fields.description }))] }));
    }
    // For non-image files, render as a download link
    return (_jsx("a", { href: file.url.startsWith("//") ? `https:${file.url}` : file.url, target: "_blank", rel: "noopener noreferrer", title: altText, children: altText || file.fileName || "Download file" }));
};
